import React from 'react';

const Resume: React.FC = () => {
  const handleDownloadResume = () => {
    // Open the GitHub resume link in a new tab
    window.open('https://github.com/SilentMic/My-resume/blob/main/KiratMehta%20Resume.pdf', '_blank');
  };

  const resumeHighlights = [
    {
      category: 'Education',
      items: [
        {
          title: 'Advanced Diploma, Computer Programmer',
          subtitle: 'Sheridan College, Ontario, Canada',
          period: 'Dec 2019 – Apr 2021',
          description: 'GPA: 3.63 overall - Comprehensive programming education covering multiple languages and technologies'
        }
      ]
    },
    {
      category: 'Key Skills',
      items: [
        {
          title: 'Technical Support',
          subtitle: 'Expert Level',
          period: '4+ Years',
          description: 'Complex problem solving and customer service excellence across multiple companies'
        },
        {
          title: 'API Integration',
          subtitle: 'Advanced',
          period: '2+ Years',
          description: 'REST and SOAP APIs for system integration and troubleshooting using Postman and Soap UI'
        },
        {
          title: 'Database Management',
          subtitle: 'Advanced',
          period: '4+ Years',
          description: 'MS SQL Server data integrity and client data management'
        }
      ]
    }
  ];

  return (
    <section id="resume" className="py-20 bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 fade-in">
            My <span className="gradient-text">Resume</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto fade-in">
            Download my resume to get a comprehensive overview of my experience, skills, and achievements
          </p>
        </div>

        {/* Resume Download Card */}
        <div className="max-w-4xl mx-auto mb-16 fade-in">
          <div className="resume-card bg-slate-800/50 backdrop-blur-sm p-8 md:p-10 rounded-xl border border-slate-700/50 text-center hover:border-primary/50 transition-all duration-300">
            <div className="mb-6">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-file-pdf text-primary text-4xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Kirat Mehta - Resume</h3>
              <p className="text-gray-400">Technical Support Analyst & Software Developer</p>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-1">4+</div>
                <div className="text-gray-400 text-xs md:text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-1">20+</div>
                <div className="text-gray-400 text-xs md:text-sm">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-1">15+</div>
                <div className="text-gray-400 text-xs md:text-sm">Technologies</div>
              </div>
            </div>

            <button
              onClick={handleDownloadResume}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 pulse-btn shadow-lg hover:shadow-primary/50"
            >
              <i className="fas fa-download mr-3"></i>
              Download Resume (PDF)
            </button>

            <p className="text-gray-500 text-sm mt-4">
              Last updated: December 2024 • File size: ~200KB
            </p>
          </div>
        </div>

        {/* Resume Highlights */}
        <div className="grid lg:grid-cols-3 gap-8">
          {resumeHighlights.map((section, sectionIndex) => (
            <div key={sectionIndex} className="fade-in">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="w-10 h-10 bg-primary rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                  {sectionIndex + 1}
                </span>
                {section.category}
              </h3>

              <div className="space-y-6">
                {section.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:bg-slate-800/50 hover:border-primary/50 transition-all duration-300"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                      <h4 className="text-lg font-semibold text-white">{item.title}</h4>
                      <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-medium self-start">
                        {item.period}
                      </span>
                    </div>
                    <p className="text-primary font-medium mb-2 text-sm">{item.subtitle}</p>
                    <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-16 fade-in">
          <div className="bg-slate-800/30 backdrop-blur-sm p-8 rounded-xl border border-slate-700/50">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center justify-center">
              <i className="fas fa-handshake text-primary mr-2"></i>
              Let's Work Together
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto text-base">
              I'm always interested in new opportunities and exciting projects. 
              If you think I'd be a good fit for your team or project, let's connect!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-primary/50"
              >
                <i className="fas fa-envelope mr-2"></i>
                Get In Touch
              </a>
              <a
                href="https://www.linkedin.com/in/kiratmehta/"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                <i className="fab fa-linkedin mr-2"></i>
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* Skills Summary */}
        <div className="mt-16 fade-in">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            Technical Proficiency
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'C#', level: 90 },
              { name: 'SQL', level: 90 },
              { name: 'React', level: 85 },
              { name: 'TypeScript', level: 75 },
              { name: 'Azure', level: 70 },
              { name: 'Git', level: 85 }
            ].map((skill, index) => (
              <div
                key={index}
                className="bg-slate-800/30 backdrop-blur-sm p-4 rounded-lg text-center border border-slate-700/50 hover:bg-slate-800/50 hover:border-primary/50 transition-all duration-300"
              >
                <div className="text-white font-semibold mb-2 text-sm">{skill.name}</div>
                <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
                <div className="text-gray-400 text-xs">{skill.level}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resume;